export type LinkInBioFormFieldKey =
  | "firstName"
  | "lastName"
  | "email"
  | "phoneNumber";

export type LinkInBioFormField = {
  field: LinkInBioFormFieldKey;
  required: boolean;
};

export const LINK_IN_BIO_FORM_FIELD_OPTIONS: Array<{
  field: LinkInBioFormFieldKey;
  label: string;
}> = [
  { field: "firstName", label: "First Name" },
  { field: "lastName", label: "Last Name" },
  { field: "email", label: "Email" },
  { field: "phoneNumber", label: "Phone Number" },
];

const LINK_IN_BIO_FORM_FIELD_KEY_SET = new Set<string>(
  LINK_IN_BIO_FORM_FIELD_OPTIONS.map((option) => option.field),
);

export const DEFAULT_LINK_IN_BIO_FORM_FIELDS: LinkInBioFormField[] =
  LINK_IN_BIO_FORM_FIELD_OPTIONS.map((option) => ({
    field: option.field,
    required: true,
  }));

export function isLinkInBioFormFieldKey(
  value: unknown,
): value is LinkInBioFormFieldKey {
  return typeof value === "string" && LINK_IN_BIO_FORM_FIELD_KEY_SET.has(value);
}

export function resolveLinkInBioFormFields({
  fields,
}: {
  fields?: Array<{ field?: unknown; required?: unknown }>;
}): LinkInBioFormField[] {
  if (!fields) {
    return DEFAULT_LINK_IN_BIO_FORM_FIELDS.map((field) => ({ ...field }));
  }

  const seen = new Set<LinkInBioFormFieldKey>();
  const resolved: LinkInBioFormField[] = [];
  for (const item of fields) {
    if (!isLinkInBioFormFieldKey(item?.field) || seen.has(item.field)) {
      continue;
    }
    seen.add(item.field);
    resolved.push({
      field: item.field,
      required: item.required !== false,
    });
  }
  return resolved;
}

export type LinkInBioFormFieldSelection = {
  requested: boolean;
  required: boolean;
};

export type LinkInBioFormFieldSelections = Record<
  LinkInBioFormFieldKey,
  LinkInBioFormFieldSelection
>;

export function defaultLinkInBioFormFieldSelections(): LinkInBioFormFieldSelections {
  return {
    firstName: { requested: true, required: true },
    lastName: { requested: true, required: true },
    email: { requested: true, required: true },
    phoneNumber: { requested: true, required: true },
  };
}

export function fieldSelectionsFromFields({
  fields,
}: {
  fields?: LinkInBioFormField[];
}): LinkInBioFormFieldSelections {
  if (!fields) {
    return defaultLinkInBioFormFieldSelections();
  }

  const byKey = new Map(fields.map((field) => [field.field, field]));
  return {
    firstName: {
      requested: byKey.has("firstName"),
      required: byKey.get("firstName")?.required !== false,
    },
    lastName: {
      requested: byKey.has("lastName"),
      required: byKey.get("lastName")?.required !== false,
    },
    email: {
      requested: byKey.has("email"),
      required: byKey.get("email")?.required !== false,
    },
    phoneNumber: {
      requested: byKey.has("phoneNumber"),
      required: byKey.get("phoneNumber")?.required !== false,
    },
  };
}

export function fieldsFromSelections({
  selections,
}: {
  selections: LinkInBioFormFieldSelections;
}): LinkInBioFormField[] {
  return LINK_IN_BIO_FORM_FIELD_OPTIONS.flatMap((option) => {
    const selection = selections[option.field];
    if (!selection.requested) return [];
    return [{ field: option.field, required: selection.required }];
  });
}
